import { useEffect, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityDetailLayout } from '../../../components/entity-detail/EntityDetailLayout.js';
import { EntityTabs } from '../../../components/entity-detail/EntityTabs.js';
import { EntityHistoryFeed } from '../../../components/entity-detail/EntityHistoryFeed.js';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import { ErrorBoundary } from '../../../shared/ErrorBoundary.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { formatDateTime } from '../../../shared/format/entity-detail.js';
import { EntityNotesTab } from '../../contacts/components/ContactNotesTab.js';
import { EntityMeetingsTab } from '../../contacts/components/ContactMeetingsTab.js';
import { EntityFilesTab } from '../../contacts/components/ContactFilesTab.js';
import { useEntityFiles } from '../../contacts/hooks/useContactFiles.js';
import { useEntityActivities } from '../../activity/hooks/useEntityActivities.js';
import { describeActivity } from '../../activity/lib/describeActivity.js';
import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import { useNotes } from '../../notes/hooks/useNotes.js';
import { TaskDetailHeader } from '../components/TaskDetailHeader.js';
import { TaskDetailSidebar } from '../components/TaskDetailSidebar.js';
import { TaskDescriptionSection } from '../components/TaskDescriptionSection.js';
import { TaskFormModal } from '../components/TaskFormModal.js';
import { RelatedTasksPanel } from '../components/RelatedTasksPanel.js';
import { useRelatedTasks } from '../hooks/useRelatedTasks.js';
import { useTask } from '../hooks/useTasks.js';
import '../components/task-detail.css';

export function TaskCardPage({ taskId }: { taskId: string }): ReactElement {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [noteFocus, setNoteFocus] = useState(0);
  const query = useTask(taskId);
  const notes = useNotes({ taskId });
  const meetings = useMeetings({ taskId });
  const files = useEntityFiles('task', taskId);
  const history = useEntityActivities('task', taskId);
  const related = useRelatedTasks(query.data ?? null);
  useEffect(() => {
    setFormOpen(false);
  }, [taskId]);
  useEffect(() => {
    if (noteFocus && params.get('tab') === 'notes')
      document.querySelector<HTMLTextAreaElement>('[data-testid="entity-note-composer"]')?.focus();
  }, [noteFocus, params]);
  const openTab = (tab: string): void => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next);
  };
  if (query.isLoading)
    return (
      <main className="p-6">
        <Skeleton active />
      </main>
    );
  if (query.isError || !query.data)
    return (
      <main className="p-6">
        <ErrorState
          title={t('crm.features.features.tasks.pages.taskcardpage.задача_не_найдена')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </main>
    );
  const task = query.data;
  const historyItems = (history.data ?? []).map((item) => ({
    id: item.id,
    text: describeActivity(item),
    date: item.occurredAt ?? item.createdAt,
  }));
  const relatedProps = {
    isLoading: related.isLoading,
    isError: related.isError,
    error: related.error,
    hasLinks: Boolean(
      task.deal || task.company || task.contact || task.document || task.documentId,
    ),
    onRetry: () => void related.refetch(),
  };
  const historyContent = history.isLoading ? (
    <Skeleton active />
  ) : history.error ? (
    <ErrorState
      title={t('crm.entity_detail.load_error')}
      error={history.error}
      onRetry={() => void history.refetch()}
    />
  ) : (
    <EntityHistoryFeed items={historyItems} />
  );
  return (
    <ErrorBoundary>
      <EntityDetailLayout
        header={
          <TaskDetailHeader
            task={task}
            onEdit={() => setFormOpen(true)}
            onNote={() => {
              openTab('notes');
              setNoteFocus((value) => value + 1);
            }}
            onHistory={() => openTab('history')}
          />
        }
        sidebar={<TaskDetailSidebar task={task} />}
        main={
          <EntityTabs
            items={[
              {
                key: 'overview',
                label: t('crm.entity_detail.overview'),
                content: (
                  <div className="task-detail-overview">
                    <section>
                      <TaskDescriptionSection taskId={task.id} description={task.description} />
                    </section>
                    <section>
                      <h2 className="entity-section-title">
                        {t('crm.features.task_detail.last_notes')}
                      </h2>
                      {notes.isLoading ? (
                        <Skeleton active />
                      ) : notes.error ? (
                        <ErrorState
                          title={t('crm.entity_detail.load_error')}
                          error={notes.error}
                          onRetry={() => void notes.refetch()}
                        />
                      ) : notes.data?.length ? (
                        notes.data.slice(0, 3).map((note) => (
                          <article key={note.id}>
                            <div className="entity-meta">
                              {note.createdByMember?.user.name ||
                                t(
                                  'crm.features.features.notes.components.entitynotespanel.неизвестный_автор',
                                )}
                              {' · '}
                              <time dateTime={note.createdAt}>
                                {formatDateTime(note.createdAt)}
                              </time>
                            </div>
                            <p className="entity-note-text">{note.body}</p>
                          </article>
                        ))
                      ) : (
                        <EmptyState text={t('crm.entity_detail.empty_notes')} />
                      )}
                    </section>
                    <section>
                      <h2 className="entity-section-title">
                        {t('crm.features.task_detail.related')}
                      </h2>
                      <RelatedTasksPanel {...relatedProps} tasks={related.tasks.slice(0, 3)} />
                    </section>
                    <section>
                      <h2 className="entity-section-title">
                        {t('crm.features.task_detail.last_activity')}
                      </h2>
                      {history.isLoading || history.error ? (
                        historyContent
                      ) : (
                        <EntityHistoryFeed items={historyItems.slice(0, 3)} />
                      )}
                    </section>
                  </div>
                ),
              },
              {
                key: 'notes',
                label: t('crm.entity_detail.notes'),
                count: notes.data?.length ?? 0,
                content: <EntityNotesTab taskId={taskId} />,
              },
              {
                key: 'meetings',
                label: t('crm.entity_detail.meetings'),
                count: meetings.data?.length ?? 0,
                content: <EntityMeetingsTab taskId={taskId} />,
              },
              {
                key: 'files',
                label: t('crm.entity_detail.files'),
                count: files.data?.length ?? 0,
                content: <EntityFilesTab taskId={taskId} />,
              },
              {
                key: 'related',
                label: t('crm.features.task_detail.related'),
                count: related.tasks.length,
                content: <RelatedTasksPanel {...relatedProps} tasks={related.tasks} />,
              },
              {
                key: 'history',
                label: t('crm.entity_detail.history'),
                count: historyItems.length,
                content: historyContent,
              },
            ]}
          />
        }
      />
      {formOpen ? <TaskFormModal open onClose={() => setFormOpen(false)} task={task} /> : null}
    </ErrorBoundary>
  );
}
